import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  BeakerIcon,
  PlusIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const SEVERITY_CONFIG = {
  major: { color: 'red', label: 'Major Interaction', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300', badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
  moderate: { color: 'amber', label: 'Moderate Interaction', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-700 dark:text-amber-300', badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' },
  minor: { color: 'sky', label: 'Minor Interaction', bg: 'bg-sky-50 dark:bg-sky-900/20', border: 'border-sky-200 dark:border-sky-800', text: 'text-sky-700 dark:text-sky-300', badge: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300' },
};

export default function MedicationCheckPage() {
  const { user, updateUser } = useAuth();
  const [medications, setMedications] = useState(() => {
    const saved = user?.medications?.filter(Boolean);
    return saved?.length >= 2 ? saved : saved?.length === 1 ? [...saved, ''] : ['', ''];
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState(false);

  // If user loads after initial render, seed from profile
  useEffect(() => {
    if (user?.medications?.length) {
      const saved = user.medications.filter(Boolean);
      setMedications(saved.length >= 2 ? saved : [...saved, '']);
    }
  }, [user?.id]); // only on user identity change, not every render

  function addMed() {
    if (medications.length < 15) setMedications([...medications, '']);
  }

  function removeMed(i) {
    if (medications.length <= 2) return;
    setMedications(medications.filter((_, idx) => idx !== i));
  }

  function updateMed(i, val) {
    const updated = [...medications];
    updated[i] = val;
    setMedications(updated);
  }

  async function handleCheck() {
    const meds = medications.map(m => m.trim()).filter(Boolean);
    if (meds.length < 1) {
      setError('Enter at least one medication.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await api.post('/premium/medication-check', { medications: meds });
      setResult(res.data);
      // Save the medication list back to the user profile
      try {
        const updated = await api.patch('/users/me', { medications: meds });
        updateUser(updated.data);
        setSavedMsg(true);
        setTimeout(() => setSavedMsg(false), 3000);
      } catch (_) {
        // non-fatal — check result still shown
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Check failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const hasInteractions = result && (result.interactions?.length > 0 || result.labFlags?.length > 0);

  if (user?.subscriptionStatus === 'starter') {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <BeakerIcon className="w-7 h-7 text-teal-500" />
        </div>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">Medication Check requires Full Access</h2>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 max-w-sm mx-auto">
          Check for interactions between your medications and see how they relate to your lab results. Available on the Full Access plan.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-sky-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-sky-700 transition-colors"
        >
          <SparklesIcon className="w-4 h-4" />
          Upgrade to Full Access — $25/mo
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <BeakerIcon className="w-5 h-5 text-teal-600" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-800 dark:text-white">Medication Interaction Check</h1>
          </div>
          {savedMsg && (
            <span className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full flex items-center gap-1">
              <CheckCircleIcon className="w-3.5 h-3.5" /> Medications saved
            </span>
          )}
        </div>
        <p className="text-slate-500 dark:text-slate-400">
          Enter your medications to check for interactions — cross-referenced with your lab values.
        </p>
      </div>

      {/* Input form */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 shadow-sm mb-6">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide mb-4">Your Medications</h2>
        <div className="space-y-3 mb-4">
          {medications.map((med, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={med}
                onChange={e => updateMed(i, e.target.value)}
                placeholder={`Medication ${i + 1} (e.g., Metformin 500mg)`}
                className="flex-1 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addMed(); } }}
              />
              {medications.length > 2 && (
                <button
                  onClick={() => removeMed(i)}
                  className="text-slate-400 hover:text-red-500 transition-colors p-1"
                  title="Remove"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={addMed}
            disabled={medications.length >= 15}
            className="flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700 font-medium disabled:text-slate-400"
          >
            <PlusIcon className="w-4 h-4" />
            Add another medication
          </button>
          <button
            onClick={handleCheck}
            disabled={loading}
            className="bg-sky-600 hover:bg-sky-700 disabled:bg-sky-300 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Checking…
              </>
            ) : (
              <>
                <BeakerIcon className="w-4 h-4" />
                Check Interactions
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Summary */}
          <div className={`p-4 rounded-xl border flex items-start gap-3 ${hasInteractions ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'}`}>
            {hasInteractions ? (
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            ) : (
              <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <p className={`font-semibold text-sm ${hasInteractions ? 'text-amber-800 dark:text-amber-200' : 'text-green-800 dark:text-green-200'}`}>
                {hasInteractions
                  ? `Found ${(result.interactions?.length || 0) + (result.labFlags?.length || 0)} concern${(result.interactions?.length || 0) + (result.labFlags?.length || 0) !== 1 ? 's' : ''} to discuss with your doctor`
                  : 'No significant interactions found'}
              </p>
              <p className={`text-xs mt-0.5 ${hasInteractions ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'}`}>
                Checked: {result.medications.join(', ')}
              </p>
            </div>
          </div>

          {/* Drug-drug interactions */}
          {result.interactions?.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-3">Drug Interactions</h2>
              <div className="space-y-3">
                {result.interactions.map((interaction, i) => {
                  const cfg = SEVERITY_CONFIG[interaction.severity] || SEVERITY_CONFIG.minor;
                  return (
                    <div key={i} className={`${cfg.bg} ${cfg.border} border rounded-xl p-4`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <p className="font-semibold text-slate-800 dark:text-white text-sm">
                          {interaction.drugs.join(' + ')}
                        </p>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.badge}`}>
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">{interaction.description}</p>
                      <div className="flex items-start gap-2">
                        <InformationCircleIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${cfg.text}`} />
                        <p className={`text-xs font-medium ${cfg.text}`}>{interaction.action}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Lab-based flags */}
          {result.labFlags?.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-3">Based on Your Lab Values</h2>
              <div className="space-y-3">
                {result.labFlags.map((flag, i) => (
                  <div key={i} className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                    <p className="font-semibold text-slate-800 dark:text-white text-sm mb-1">{flag.medication}</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">{flag.concern}</p>
                    <div className="flex items-start gap-2">
                      <InformationCircleIcon className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs font-medium text-purple-700 dark:text-purple-300">{flag.action}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* General tips */}
          {result.generalTips?.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-3">Medication Safety Tips</h2>
              <ul className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl divide-y divide-slate-100 dark:divide-slate-700">
                {result.generalTips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 px-4 py-3">
                    <CheckCircleIcon className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700 dark:text-slate-300">{tip}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Disclaimer */}
          <div className="flex items-start gap-2 p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl">
            <ShieldCheckIcon className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500 dark:text-slate-400">{result.disclaimer}</p>
          </div>
        </div>
      )}
    </div>
  );
}
